/** Korean presentation formats used across the design. Pure functions (local time zone). */

const WEEKDAYS = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function dayDiff(value: Date, now: Date): number {
  return Math.round((startOfDay(now) - startOfDay(value)) / 86_400_000);
}

/** "오전 10:42" / "오후 4:02" */
export function formatClock(value: string | Date): string {
  const date = toDate(value);
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours < 12 ? '오전' : '오후'} ${hours % 12 || 12}:${minutes}`;
}

/** Conversation list: clock today, "어제", weekday within a week, "10월 2일", or with year. */
export function formatListTime(value: string | Date, now: Date = new Date()): string {
  const date = toDate(value);
  const diff = dayDiff(date, now);
  if (diff <= 0) return formatClock(date);
  if (diff === 1) return '어제';
  if (diff < 7) return WEEKDAYS[date.getDay()]!;
  if (date.getFullYear() === now.getFullYear()) return `${date.getMonth() + 1}월 ${date.getDate()}일`;
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}

/** Timeline divider: "오늘", "어제", "2026년 10월 8일 목요일". */
export function formatDayDivider(value: string | Date, now: Date = new Date()): string {
  const date = toDate(value);
  const diff = dayDiff(date, now);
  if (diff === 0) return '오늘';
  if (diff === 1) return '어제';
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 ${WEEKDAYS[date.getDay()]}`;
}

/** A time ahead, as "예약 전송" says it: "오늘 오후 6:00", "내일 오전 9:00", "10월 28일 화요일 오전 9:00". */
export function formatWhen(value: string | Date, now: Date = new Date()): string {
  const date = toDate(value);
  const diff = dayDiff(date, now);
  const day = diff === 0 ? '오늘' : diff === -1 ? '내일' : `${date.getFullYear() === now.getFullYear() ? '' : `${date.getFullYear()}년 `}${date.getMonth() + 1}월 ${date.getDate()}일 ${WEEKDAYS[date.getDay()]}`;
  return `${day} ${formatClock(date)}`;
}

/** The usual times to schedule for: this evening while it is still ahead, tomorrow morning, next Monday morning. */
export function scheduleChoices(now: Date): Date[] {
  const at = (days: number, hour: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, hour, 0, 0, 0);
  const evening = at(0, 18);
  const monday = at(((8 - now.getDay()) % 7) || 7, 9);
  const choices = [evening, at(1, 9), monday].filter((d) => d.getTime() - now.getTime() >= 10 * 60_000);
  return choices.filter((d, i) => choices.findIndex((other) => other.getTime() === d.getTime()) === i);
}

/** Stable local-day key for grouping messages. */
export function dayKey(value: string | Date): string {
  const date = toDate(value);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/** "4.2MB", "86KB", "1.1GB" (design fmtSize, 1024-based). */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)}GB`;
  if (bytes > 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

export type FileBadge = 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'hwp' | 'zip' | 'png' | 'other';

/** Colour class for `.fc-ic` plus the short label shown inside it. */
export function fileBadge(ext: string): { cls: FileBadge; label: string } {
  const e = ext.toLowerCase();
  const label = (e || 'FILE').slice(0, 4).toUpperCase();
  if (e === 'pdf') return { cls: 'pdf', label };
  if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(e)) return { cls: 'xlsx', label };
  if (['ppt', 'pptx', 'odp', 'key'].includes(e)) return { cls: 'pptx', label };
  if (['hwp', 'hwpx'].includes(e)) return { cls: 'hwp', label };
  if (['zip', '7z', 'tar', 'gz', 'tgz'].includes(e)) return { cls: 'zip', label };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'bmp', 'svg'].includes(e)) return { cls: 'png', label };
  if (['doc', 'docx', 'txt', 'md', 'rtf', 'odt', 'pages'].includes(e)) return { cls: 'docx', label };
  return { cls: 'other', label };
}

/** Host part of a server URL ("messenger.share-dev.example"); falls back to the input. */
export function hostOf(serverUrl: string): string {
  try {
    return new URL(serverUrl).host;
  } catch {
    return serverUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  }
}
